import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProxyConnection } from '../context/ProxyConnectionContext';
import { isSuperAdminUser } from '../utils/superAdmin';
import {
  captureOnboardingFromUrl,
  markProxySetupComplete,
  shouldShowPostRegistrationWalkthrough,
} from '../utils/onboardingStorage';
import {
  detectProxyAgentPlatform,
  DESKTOP_AGENT_NAME,
  desktopAgentInstallHint,
  downloadProxyAgent,
  proxyPlatformLabel,
  type ProxyAgentPlatform,
} from '../utils/proxyAgentDownload';

const SHOW_PROXY_WALKTHROUGH_EVENT = 'crud:show-proxy-walkthrough';
const WALKTHROUGH_PROXY_POLL_MS = 2000;

const WALKTHROUGH_DIALOG_Z_INDEX = (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 10;

const DIALOG_WIDTH = 'min(400px, calc(100vw - 32px))';

type ChecklistStepProps = {
  done: boolean;
  checking?: boolean;
  title: string;
  detail?: string;
  action?: React.ReactNode;
  isLast?: boolean;
};

const ChecklistStep: React.FC<ChecklistStepProps> = ({ done, checking, title, detail, action, isLast }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1.25,
      py: 1.5,
      borderBottom: isLast ? 'none' : '1px solid',
      borderColor: (theme) => alpha(theme.palette.divider, 0.6),
    }}
  >
    <Box
      sx={{
        mt: 0.125,
        flexShrink: 0,
        width: 22,
        height: 22,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {done ? (
        <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
      ) : checking ? (
        <CircularProgress size={18} thickness={5} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
      )}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{
          color: done ? 'text.secondary' : 'text.primary',
          textDecoration: done ? 'line-through' : 'none',
          lineHeight: 1.4,
        }}
      >
        {title}
      </Typography>
      {detail && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, lineHeight: 1.5 }}>
          {detail}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1.25 }}>{action}</Box>}
    </Box>
  </Box>
);

const PostRegistrationWalkthrough: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isConnected, refreshProxyStatus, isChecking } = useProxyConnection();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [platform] = useState<ProxyAgentPlatform>(() => detectProxyAgentPlatform());
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isPollingAgent, setIsPollingAgent] = useState(false);

  const pollDesktopAgent = useCallback(async () => {
    setIsPollingAgent(true);
    try {
      await refreshProxyStatus({ background: true });
    } finally {
      setIsPollingAgent(false);
    }
  }, [refreshProxyStatus]);

  const isSuperAdmin = isSuperAdminUser(user);
  const isAdminOnly = user?.role === 'ADMIN';

  const syncWalkthroughOpen = useCallback(() => {
    captureOnboardingFromUrl();
    if (authLoading || !isAuthenticated || isSuperAdmin || isAdminOnly || !user?.id) {
      setOpen(false);
      return;
    }
    // New-account onboarding only. Never trap returning users behind a blocking
    // dialog just because the desktop agent is disconnected.
    if (shouldShowPostRegistrationWalkthrough(user.id)) {
      setOpen(true);
      return;
    }
    setOpen(false);
  }, [authLoading, isAuthenticated, isSuperAdmin, isAdminOnly, user?.id]);

  useEffect(() => {
    syncWalkthroughOpen();
  }, [syncWalkthroughOpen, location.pathname, location.search]);

  useEffect(() => {
    const handleShowWalkthrough = () => {
      if (authLoading || !isAuthenticated || isSuperAdmin || isAdminOnly || !user?.id) return;
      setOpen(true);
    };
    window.addEventListener(SHOW_PROXY_WALKTHROUGH_EVENT, handleShowWalkthrough);
    return () => window.removeEventListener(SHOW_PROXY_WALKTHROUGH_EVENT, handleShowWalkthrough);
  }, [authLoading, isAuthenticated, isSuperAdmin, isAdminOnly, user?.id]);

  useEffect(() => {
    if (!open || isConnected) return;

    void pollDesktopAgent();
    const id = window.setInterval(() => {
      void pollDesktopAgent();
    }, WALKTHROUGH_PROXY_POLL_MS);

    return () => window.clearInterval(id);
  }, [open, isConnected, pollDesktopAgent]);

  useEffect(() => {
    if (!open || isConnected || !downloadStarted) return;
    void pollDesktopAgent();
  }, [downloadStarted, open, isConnected, pollDesktopAgent]);

  useEffect(() => {
    if (isConnected && open && user?.id) {
      markProxySetupComplete(user.id);
    }
  }, [isConnected, open, user?.id]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      await downloadProxyAgent(platform);
      setDownloadStarted(true);
    } finally {
      setDownloading(false);
    }
  }, [platform]);

  const handleDismiss = useCallback(() => {
    if (user?.id) {
      markProxySetupComplete(user.id);
    }
    setOpen(false);
    window.dispatchEvent(new Event('crud:proxy-setup-complete'));
  }, [user?.id]);

  if (!open) return null;

  const stepDownloadDone = downloadStarted || isConnected;
  const stepInstallDone = isConnected;
  const stepReadyDone = isConnected;
  const isDetectingAgent = !isConnected && (isPollingAgent || isChecking);

  return (
    <Dialog
      open={open}
      className="proxy-setup-dialog"
      maxWidth={false}
      fullWidth={false}
      disableEscapeKeyDown={false}
      onClose={() => handleDismiss()}
      aria-labelledby="proxy-setup-title"
      sx={{
        zIndex: WALKTHROUGH_DIALOG_Z_INDEX,
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(15, 23, 42, 0.42)',
          backdropFilter: 'blur(3px)',
        },
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        },
        '& .MuiDialog-paper': {
          width: DIALOG_WIDTH,
          maxWidth: DIALOG_WIDTH,
          margin: 0,
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.18), 0 8px 16px rgba(15, 23, 42, 0.08)',
        },
      }}
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle
        id="proxy-setup-title"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          px: 2.5,
          pt: 2.5,
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.125rem', letterSpacing: '-0.01em' }}>
            Almost there!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.5, maxWidth: 320 }}>
            Follow these steps to send requests through the {DESKTOP_AGENT_NAME}.
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Close"
          onClick={handleDismiss}
          sx={{
            mt: -0.25,
            bgcolor: (theme) => alpha(theme.palette.action.hover, 0.6),
            '&:hover': { bgcolor: 'action.selected' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 0.5, pb: 1 }}>
        <Box
          sx={{
            borderRadius: '12px',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.divider, 0.8),
            px: 1.5,
            py: 0.25,
          }}
        >
          <ChecklistStep
            done={stepDownloadDone}
            title={`Download the ${DESKTOP_AGENT_NAME} (${proxyPlatformLabel(platform)})`}
            action={
              !stepDownloadDone ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={downloading ? <CircularProgress size={14} /> : <DownloadIcon />}
                  disabled={downloading}
                  onClick={() => void handleDownload()}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 500,
                    bgcolor: 'background.paper',
                  }}
                >
                  {downloading ? 'Downloading…' : 'Download'}
                </Button>
              ) : undefined
            }
          />
          <ChecklistStep
            done={stepInstallDone}
            checking={!stepInstallDone && isDetectingAgent && stepDownloadDone}
            title={`Install and run the ${DESKTOP_AGENT_NAME}`}
            detail={
              stepInstallDone
                ? `${DESKTOP_AGENT_NAME} is running on http://localhost:9000.`
                : desktopAgentInstallHint(platform)
            }
          />
          <ChecklistStep
            done={stepReadyDone}
            checking={!stepReadyDone && isDetectingAgent}
            isLast
            title="Start using CRUD Expert"
            detail={
              isConnected
                ? `${DESKTOP_AGENT_NAME} is connected — you can use Collections and Test Plans.`
                : isDetectingAgent
                  ? `Checking for the ${DESKTOP_AGENT_NAME} on http://localhost:9000…`
                  : `Waiting for the ${DESKTOP_AGENT_NAME} on http://localhost:9000.`
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pt: 1, pb: 2.5 }}>
        <Button
          variant="contained"
          fullWidth
          disableElevation
          onClick={handleDismiss}
          sx={{
            textTransform: 'none',
            py: 1.1,
            borderRadius: '12px',
            fontWeight: 600,
            fontSize: '0.9375rem',
            boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)',
          }}
        >
          {isConnected ? 'Got it' : 'Skip for now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostRegistrationWalkthrough;
